import type { CutSticker } from './stickers';
import { type Swatch, type SwatchName, type ToneName, swatches } from './tokens';

/**
 * A line icon of a subject (design v4): the name of a Lucide icon, so the web and the app pick the
 * same picture from their own Lucide package.
 */
export type SubjectIcon =
  | 'Dumbbell'
  | 'Sigma'
  | 'Triangle'
  | 'Atom'
  | 'Telescope'
  | 'FlaskConical'
  | 'Leaf'
  | 'Sprout'
  | 'Earth'
  | 'Map'
  | 'Landmark'
  | 'Library'
  | 'BookOpen'
  | 'Languages'
  | 'PenLine'
  | 'Monitor'
  | 'Palette'
  | 'Music'
  | 'Hammer'
  | 'Shield'
  | 'HeartPulse'
  | 'Scale'
  | 'BookMarked';

export interface SubjectLook {
  swatch: SwatchName;
  colors: Swatch;
  sticker: CutSticker;
  /** v4: the pastel tone and the line icon that replace the sticker */
  tone: ToneName;
  icon: SubjectIcon;
}

// five pastel families for subjects; lavender is the accent's and belongs to no subject
// order matters: «Фізична культура» before «Фізика», foreign languages before «мова»
const RULES: ReadonlyArray<[RegExp, SwatchName, CutSticker, ToneName, SubjectIcon]> = [
  [/фізичн|фізкульт/i, 'tangerine', 'soccer-ball', 'orange', 'Dumbbell'],
  [/алгебр|математ/i, 'sky', 'abacus', 'blue', 'Sigma'],
  [/геометр/i, 'sky', 'triangular-ruler', 'blue', 'Triangle'],
  [/фізик/i, 'sky', 'atom-symbol', 'violet', 'Atom'],
  [/астроном/i, 'sky', 'telescope', 'violet', 'Telescope'],
  [/хімі/i, 'mint', 'test-tube', 'teal', 'FlaskConical'],
  [/біолог/i, 'mint', 'dna', 'green', 'Leaf'],
  [/природ|досліджую світ/i, 'mint', 'seedling', 'green', 'Sprout'],
  [/географ/i, 'mint', 'globe-showing-europe-africa', 'teal', 'Earth'],
  [/всесвітн/i, 'tangerine', 'world-map', 'orange', 'Map'],
  [/істор/i, 'tangerine', 'classical-building', 'orange', 'Landmark'],
  [/зарубіжн/i, 'pink', 'books', 'pink', 'Library'],
  [/літератур|читання/i, 'pink', 'open-book', 'pink', 'BookOpen'],
  [
    /англ|іноземн|німецьк|французьк|польськ|іспанськ/i,
    'pink',
    'speech-balloon',
    'red',
    'Languages',
  ],
  [/мов|грамот/i, 'sun', 'fountain-pen', 'yellow', 'PenLine'],
  [/інформат/i, 'sky', 'laptop', 'teal', 'Monitor'],
  [/музи/i, 'pink', 'artist-palette', 'violet', 'Music'],
  [/мистецтв|образотвор/i, 'pink', 'artist-palette', 'pink', 'Palette'],
  [/технолог|трудов/i, 'tangerine', 'hammer', 'orange', 'Hammer'],
  [/захист україни|допризов/i, 'sky', 'shield', 'blue', 'Shield'],
  [/здоров/i, 'mint', 'red-apple', 'green', 'HeartPulse'],
  [/правознав|громадян|етик|економ/i, 'sun', 'scroll', 'yellow', 'Scale'],
];

const FALLBACK: ReadonlyArray<[SwatchName, ToneName]> = [
  ['sky', 'blue'],
  ['sun', 'yellow'],
  ['mint', 'green'],
  ['tangerine', 'orange'],
  ['pink', 'pink'],
];

/** The colour, the sticker and the icon of a subject, the same on every screen and on the web. */
export function subjectLook(subject: string): SubjectLook {
  for (const [pattern, swatch, sticker, tone, icon] of RULES) {
    if (pattern.test(subject)) return { swatch, colors: swatches[swatch], sticker, tone, icon };
  }
  let hash = 0;
  for (const ch of subject) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  const [swatch, tone] = FALLBACK[hash % FALLBACK.length] as [SwatchName, ToneName];
  return { swatch, colors: swatches[swatch], sticker: 'books', tone, icon: 'BookMarked' };
}

/** «Українська література» → «Укр. літ.»: for a week grid where a cell is narrow. */
export function subjectShort(subject: string): string {
  const known: Record<string, string> = {
    'Українська мова': 'Укр. мова',
    'Українська література': 'Укр. літ.',
    'Зарубіжна література': 'Зар. літ.',
    'Англійська мова': 'Англ. мова',
    'Історія України': 'Іст. Укр.',
    'Всесвітня історія': 'Всесв. іст.',
    'Фізична культура': 'Фізкульт.',
    'Здоров’я, безпека та добробут': 'Здоров’я',
    'Алгебра і початки аналізу': 'Алгебра',
    'Біологія і екологія': 'Біологія',
    'Дизайн і технології': 'Технології',
    'Я досліджую світ': 'ЯДС',
    'Пізнаємо природу': 'Природа',
    'Захист України': 'Захист Укр.',
    Інформатика: 'Інформ.',
    Математика: 'Матем.',
  };
  return known[subject] ?? subject;
}
